import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, X, TrendingUp, BarChart3, Globe } from 'lucide-react';

// Country data with flags and GDP growth
const COUNTRIES = [
  { code: 'VN', name: 'Vietnam', nameEn: 'Vietnam', flag: '🇻🇳', gdpGrowth: '6.5%', color: 'from-red-500 to-yellow-500' },
  { code: 'NSE', name: 'Inde', nameEn: 'India', flag: '🇮🇳', gdpGrowth: '7.2%', color: 'from-orange-500 to-green-500' },
  { code: 'PSE', name: 'Philippines', nameEn: 'Philippines', flag: '🇵🇭', gdpGrowth: '5.8%', color: 'from-blue-500 to-red-500' },
  { code: 'JK', name: 'Indonésie', nameEn: 'Indonesia', flag: '🇮🇩', gdpGrowth: '5.1%', color: 'from-red-500 to-white' },
  { code: 'WSE', name: 'Pologne', nameEn: 'Poland', flag: '🇵🇱', gdpGrowth: '3.2%', color: 'from-white to-red-500' },
  { code: 'KRX', name: 'Corée du Sud', nameEn: 'South Korea', flag: '🇰🇷', gdpGrowth: '2.1%', color: 'from-blue-500 to-red-500' },
];

// Step components
const Step1MarketSelection = ({ selectedMarket, onSelectMarket }) => (
  <div className="space-y-6">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-white mb-2">Bienvenue sur VALORAMAP</h2>
      <p className="text-gray-400">Commencez par choisir un marché émergent</p>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {COUNTRIES.map((country) => (
        <button
          key={country.code}
          onClick={() => onSelectMarket(country.code)}
          className={`p-4 rounded-xl border-2 transition-all hover:scale-105 ${
            selectedMarket === country.code
              ? 'border-emerald-500 bg-emerald-500/20'
              : 'border-white/10 bg-white/5 hover:border-white/30'
          }`}
          data-testid={`onboarding-country-${country.code}`}
        >
          <div className="text-4xl mb-2">{country.flag}</div>
          <div className="text-white font-medium">{country.name}</div>
          <div className="text-emerald-400 text-sm flex items-center justify-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {country.gdpGrowth} croissance
          </div>
        </button>
      ))}
    </div>
  </div>
);

const Step2Filters = () => (
  <div className="space-y-6">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-white mb-2">Trouvez les actions sous-évaluées</h2>
      <p className="text-gray-400">Utilisez les filtres de valorisation pour identifier les opportunités</p>
    </div>
    
    <div className="space-y-4">
      {/* P/E Ratio */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <span className="text-blue-400 font-bold">P/E</span>
          </div>
          <div>
            <h3 className="text-white font-medium">Ratio Prix/Bénéfices</h3>
            <p className="text-sm text-gray-400">Price-to-Earnings Ratio</p>
          </div>
        </div>
        <p className="text-gray-300 text-sm">
          Plus le P/E est <span className="text-emerald-400 font-medium">bas</span>, moins l'action est chère par rapport à ses bénéfices.
          Un P/E de 10 signifie que vous payez 10€ pour chaque 1€ de bénéfice.
        </p>
      </div>
      
      {/* P/B Ratio */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <span className="text-purple-400 font-bold">P/B</span>
          </div>
          <div>
            <h3 className="text-white font-medium">Ratio Prix/Valeur Comptable</h3>
            <p className="text-sm text-gray-400">Price-to-Book Ratio</p>
          </div>
        </div>
        <p className="text-gray-300 text-sm">
          Un P/B <span className="text-emerald-400 font-medium">inférieur à 1</span> signifie que l'action vaut moins que ses actifs nets.
          Potentiel de "marge de sécurité" selon Benjamin Graham.
        </p>
      </div>
      
      {/* ROE */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <span className="text-emerald-400 font-bold">ROE</span>
          </div>
          <div>
            <h3 className="text-white font-medium">Retour sur Capitaux Propres</h3>
            <p className="text-sm text-gray-400">Return on Equity</p>
          </div>
        </div>
        <p className="text-gray-300 text-sm">
          Plus le ROE est <span className="text-emerald-400 font-medium">élevé</span>, plus l'entreprise est rentable.
          Un ROE &gt; 15% indique généralement une entreprise performante.
        </p>
      </div>
    </div>
  </div>
);

const Step3MacroContext = () => (
  <div className="space-y-6">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-white mb-2">Comprenez l'économie avant d'investir</h2>
      <p className="text-gray-400">Chaque pays a son contexte macroéconomique</p>
    </div>
    
    <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <Globe className="w-8 h-8 text-emerald-400" />
        <div>
          <h3 className="text-white font-medium">Widget Macro-économique</h3>
          <p className="text-gray-400 text-sm">Données clés par pays</p>
        </div>
      </div>
      
      {/* Mock MacroWidget preview */}
      <div className="bg-black/30 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-emerald-400 text-lg font-bold">6.5%</div>
            <div className="text-gray-500 text-xs">PIB Growth</div>
          </div>
          <div>
            <div className="text-amber-400 text-lg font-bold">3.2%</div>
            <div className="text-gray-500 text-xs">Inflation</div>
          </div>
          <div>
            <div className="text-blue-400 text-lg font-bold">4.5%</div>
            <div className="text-gray-500 text-xs">Taux Directeur</div>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-3">
          <p className="text-gray-400 text-xs text-center">
            💡 Cliquez sur le widget pour voir l'analyse détaillée
          </p>
        </div>
      </div>
      
      <p className="text-gray-300 text-sm mt-4">
        Consultez <span className="text-emerald-400">PIB, inflation, taux directeur</span> avant de sélectionner vos actions.
        Le contexte macro influence directement la valorisation des entreprises.
      </p>
    </div>
    
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <BarChart3 className="w-5 h-5 text-amber-400 mt-0.5" />
        <div>
          <p className="text-amber-200 text-sm font-medium">Conseil Pro</p>
          <p className="text-gray-300 text-sm">
            Un pays avec une croissance forte (+5%) et une inflation maîtrisée (&lt;5%) 
            offre généralement un meilleur environnement pour les small-caps.
          </p>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Onboarding Modal - Shows only on first login
 * 3 steps: Market Selection → Filters Education → Macro Context
 */
export const OnboardingModal = ({ isOpen, onClose, onComplete, initialMarket = null }) => {
  const [step, setStep] = useState(1);
  const [selectedMarket, setSelectedMarket] = useState(initialMarket);
  
  const totalSteps = 3;
  
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const handleComplete = () => {
    onComplete(selectedMarket);
    onClose();
  };
  
  const handleSkip = () => {
    onComplete(null);
    onClose();
  };
  
  const handleMarketSelect = (market) => {
    setSelectedMarket(market);
    // Auto-advance after selecting a market
    setTimeout(() => setStep(2), 300);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent 
        className="sm:max-w-2xl bg-gray-900 border-gray-700 p-0 overflow-hidden"
        data-testid="onboarding-modal"
      >
        {/* Header with progress */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {step > 1 && (
              <button 
                onClick={handleBack}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="onboarding-back"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
            )}
            <span className="text-gray-400 text-sm">Étape {step}/{totalSteps}</span>
          </div>
          
          {/* Progress dots */}
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-colors ${
                  s === step ? 'bg-emerald-400' : s < step ? 'bg-emerald-400/50' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
          
          <button 
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            data-testid="onboarding-skip"
          >
            Passer
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <Step1MarketSelection 
              selectedMarket={selectedMarket} 
              onSelectMarket={handleMarketSelect}
            />
          )}
          {step === 2 && <Step2Filters />}
          {step === 3 && <Step3MacroContext />}
        </div>
        
        {/* Footer with navigation */}
        <div className="p-4 border-t border-white/10 flex justify-between">
          <div>
            {step === 1 && selectedMarket && (
              <span className="text-emerald-400 text-sm">
                ✓ {COUNTRIES.find(c => c.code === selectedMarket)?.name} sélectionné
              </span>
            )}
          </div>
          
          <div className="flex gap-3">
            {step < totalSteps ? (
              <Button 
                onClick={handleNext}
                disabled={step === 1 && !selectedMarket}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="onboarding-next"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleComplete}
                className="bg-emerald-600 hover:bg-emerald-700 px-6"
                data-testid="onboarding-complete"
              >
                C'est parti →
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
